import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { logger } from '@app/core/helpers/logger';
import { LibrarySidebarService } from '@app/library/sidebars/library-sidebar/library-sidebar.service';
import { NotFoundError } from '@app/lingo2-errors';
import { loadMeeting, setCurrentItem } from '@app/store/actions/content.actions';
import { getMeeting } from '@app/store/reducers/content.reducer';
import { getLibraryRouteData } from '@app/store/reducers/library.reducer';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import { IContentBlock } from '@core/services/interfaces';
import { WebsocketService } from '@core/websocket';
import { ChangableComponent } from '@models/changable.component';
import { LibraryRouter } from '@models/library.router';
import { Store } from '@ngrx/store';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import {
  BillingMeetingChargingEnum,
  ImageSizeEnum,
  Meeting,
  MeetingAcceptStatusEnum,
  MeetingBillingStatusEnum,
  MeetingDetailsType,
  MeetingParticipant,
  MeetingRoleEnum,
  Router as ContentRouter,
  User,
  UserService,
  UserStatusEnum,
} from 'lingo2-models';
import { DateFnsConfigurationService } from 'lingo2-ngx-date-fns';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, of, Subject, zip } from 'rxjs';
import { debounceTime, filter, mergeAll, switchMap, takeUntil, tap } from 'rxjs/operators';
import striptags from 'striptags';
import { RESPONSE } from '../../../express.tokens';
import {
  AccountService,
  AuthService,
  ContextService,
  FilesService,
  fullUserServiceDetails,
  IFileType,
  IImageThumbnail,
  LanguageService,
  MeetingsService,
  MeetingsServicesService,
  MetaService,
  PlatformService,
  RequestService,
  UserServicesService,
} from '../../core/services/';
import { ChatService } from '@app/core/services/chat.service';

type MeetingPageViewType = 'loading' | 'not-found' | 'meeting' | 'user-service';

const meetingDetails: MeetingDetailsType[] = [
  'id',
  'slug',
  'title',
  'description',
  'keywords',
  'price_tier',
  'begin',
  'type',
  'access',
  'status',
  'charging',
  'begin_at',
  'end_at',
  'duration',
  'started_at',
  'finished_at',
  'subject_id',
  'subject',
  'level_id',
  'level',
  'language_id',
  'language',
  'cover:md:lg',
  'author:sm', // ?? 'author:md'
  'participants_limit',
  'participants_count',
  'visit_info',
  'classroom_id',
  'has',
  'can',
  'is',
  'school',
];

@Component({
  selector: 'app-meeting-page',
  templateUrl: './meeting-page.component.html',
  styleUrls: ['./meeting-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingPageComponent extends ChangableComponent implements OnInit, OnDestroy {
  public me: User;
  public meeting: Meeting;
  public userService: UserService;
  public view: MeetingPageViewType = 'loading';

  public participants: IContentBlock<MeetingParticipant, any> = {
    loading: false,
    loaded: false,
    items: [],
    filter: {},
    pagination: {
      page: 1,
      total: 0,
      totalPages: 0,
      pageSize: 10,
    },
  };
  public accountRoute = AccountService.accountRoute;
  private meetingLoaded$ = this.register(new Subject<boolean>());
  private userServiceLoaded$ = this.register(new Subject<boolean>());
  private reloadMeeting$ = this.register(new Subject<boolean>());
  private reloadUserService$ = this.register(new Subject<boolean>());
  private id: string;
  private slug: string;
  private view_processed = false;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private route: ActivatedRoute,
    private router: Router,
    private chatService: ChatService,
    private contextService: ContextService,
    private meetingsServicesService: MeetingsServicesService,
    private meetingsService: MeetingsService,
    private userServicesService: UserServicesService,
    private websocketService: WebsocketService,
    private loadingBar: LoadingBarService,
    private languageService: LanguageService,
    private request: RequestService,
    private meta: MetaService,
    private store: Store,
    private dateConfig: DateFnsConfigurationService,
    private filesService: FilesService,
    @Optional() @Inject(RESPONSE) private response: any,
    public deviceService: DeviceDetectorService,
    private readonly sidebarService: LibrarySidebarService,
    protected readonly cdr: ChangeDetectorRef,
    protected readonly platform: PlatformService,
  ) {
    super(cdr, platform);
  }

  public ngOnInit() {
    of(
      this.watchForMeta$, // Установки meta
      this.watchMe$, // при логине/перелогине - перегрузить митинг
      this.watchRouteChanged$, // При смене роута - загрузить митинг по slug
      this.watchMeetingLoaded$, // Действия после загрузки митинга
      this.watchReloadMeeting$, // Перегрузка митинга при изменениях

      this.watchUserServiceLoaded$, // Действия после загрузки индивидуального занятия
      this.watchReloadUserService$, // Перегрузка индивидуального занятия при изменениях
    )
      .pipe(mergeAll(), takeUntil(this.destroyed$))
      .subscribe({
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.stopWatching();
    this.meta.reset();
  }

  /**
   * При смене роута - загрузить митинг по slug
   */
  private get watchRouteChanged$(): Observable<any> {
    return zip(this.contextService.me$, this.languageService.language$).pipe(
      switchMap(() => {
        logger.debug('MeetingPageComponent:watchRouteChanged$ -> me+language loaded');
        return this.store.select(getLibraryRouteData).pipe(
          filter((d) => !!d),
          tap((data) => {
            logger.debug('MeetingPageComponent:watchRouteChanged$ -> route.paramMap changed');
            this.slug = data.item;
            this.loadBySlug();
            // this.loadBySlugFromStore(); // <- не работает из за странной логики митинг/юзер-сервис
          }),
          takeUntil(this.destroyed$),
        );
      }),
      takeUntil(this.destroyed$),
    );
  }

  /**
   * При логине/перелогине - права пользователя могут поменяться
   */
  private get watchMe$(): Observable<User> {
    // при логине/перелогине - права пользователя могут поменяться
    return this.contextService.me$.pipe(
      tap((me) => {
        if (me?.status !== UserStatusEnum.guest) {
          const oldMeId = this.me ? this.me.id : null;
          const newMeId = me ? me.id : null;
          this.me = me;
          if (oldMeId && oldMeId !== newMeId) {
            this.reload();
          }
        }
      }),
    );
  }

  /**
   * Действия после загрузки митинга
   */
  private get watchMeetingLoaded$(): Observable<boolean> {
    return this.meetingLoaded$.pipe(
      tap(() => {
        this.view = 'meeting';
        this.store.dispatch(setCurrentItem({ item: this.meeting }));
        this.sidebarService.setUser(this.meeting.author);
        this.setupMeta();
        this.loadParticipants();
        this.detectChanges();

        this.onBrowserOnly(() => {
          this.setTimeout(() => {
            this.incrementView();
          }, 2500);
        });
      }),
    );
  }

  /**
   * Действия после загрузки индивидуального занятия
   */
  private get watchUserServiceLoaded$(): Observable<boolean> {
    return this.userServiceLoaded$.pipe(
      tap(() => {
        this.view = 'user-service';
        this.sidebarService.setUser(this.userService.author);
        this.setupMeta();
        this.detectChanges();
      }),
    );
  }

  /**
   * Ожидание данных для установки meta
   */
  private get watchForMeta$(): Observable<any> {
    return this.meetingLoaded$.pipe(tap(() => this.setupMeta()));
  }

  private get watchReloadMeeting$(): Observable<any> {
    return this.reloadMeeting$.pipe(
      filter(() => !!this.id),
      debounceTime(2543),
      tap(() => this.loadMeetingById()),
    );
  }

  private get watchReloadUserService$(): Observable<any> {
    return this.reloadUserService$.pipe(
      filter(() => !!this.id),
      debounceTime(2543),
      tap(() => this.loadUserServiceById()),
    );
  }

  private get watchMeeting$(): Observable<Meeting> {
    return this.store.select(getMeeting, { slug: this.slug }).pipe(
      tap((meeting) => {
        if (!meeting) {
          this.view = 'loading';
          this.loadingBar.fetchingStart(35);
          return;
        }

        if (this.meeting?.id !== meeting.id) {
          // Meeting was loaded
          this.loadingBar.fetchingComplete();
          this.meeting = meeting;
          this.loadCoverImage(this.meeting?.cover_id);
          this.meetingLoaded$.next(true);
        }
      }),
      takeUntil(this.destroyed$),
    );
  }

  protected loadBySlugFromStore() {
    this.store.dispatch(loadMeeting({ slug: this.slug, meeting_id: this.id }));
  }

  /** @deprecated */
  protected loadBySlug() {
    if (!this.slug) {
      return;
    }

    this.view = 'loading';
    this.loadingBar.fetchingStart(35);
    this.meetingsServicesService
      .getBySlug(this.slug, meetingDetails, fullUserServiceDetails)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.loadingBar.fetchingComplete();
          if (response.meeting) {
            this.id = response.meeting.id;
            this.meeting = response.meeting;
            this.meetingLoaded$.next(true);
            this.startWatching();
            this.loadCoverImage(this.meeting?.cover_id);
          }
          if (response.userService) {
            this.id = response.userService.id;
            this.userService = response.userService;
            this.userServiceLoaded$.next(true);
            this.loadCoverImage(this.userService?.cover_id);
          }
        },
        error: (error: any) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loadingBar.fetchingStop();
        },
      });
  }

  /** @deprecated */
  protected loadMeetingById() {
    this.view = 'loading';
    this.loadingBar.fetchingStart(35);
    this.meetingsService
      .getMeetingById(this.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (meeting: Meeting) => {
          this.loadingBar.fetchingComplete();
          this.meeting = meeting;
          this.meetingLoaded$.next(true);
          this.loadCoverImage(this.meeting?.cover_id);
        },
        error: (error: any) => {
          this.loadingBar.fetchingStop();
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  protected loadParticipants(nextPage = false) {
    if (!this.isAuthor) {
      return;
    }

    this.participants.loading = true;
    this.loadingBar.fetchingStart(35);
    if (nextPage && this.participants.pagination.page < this.participants.pagination.totalPages) {
      this.participants.pagination.page = this.participants.pagination.page + 1;
    }
    this.meetingsService
      .getMeetingParticipants(this.id, this.participants.pagination)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (nextPage) {
            response.results.map((t) => {
              if (!this.participants.items.find((t1) => t1.id === t.id)) {
                this.participants.items.push(t);
              }
            });
          } else {
            this.participants.items = response.results;
          }

          this.participants.pagination.total = response.total;
          this.participants.pagination.totalPages = response.totalPages;
          this.participants.pagination.page = response.page;
          this.participants.loaded = true;
          this.participants.loading = false;
          this.loadingBar.fetchingComplete();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.participants.loading = false;
          this.loadingBar.fetchingStop();
        },
      });
  }

  public nextParticipantsPage() {
    this.loadParticipants(true);
  }

  // функция для трекинга списка, чтобы он не 'моргал'
  public trackParticipantByFn(index, item: MeetingParticipant): string {
    return item.id;
  }

  protected loadUserServiceById() {
    this.view = 'loading';
    this.loadingBar.fetchingStart(35);
    this.userServicesService
      .getById(this.id, fullUserServiceDetails)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (userService: UserService) => {
          this.loadingBar.fetchingComplete();
          this.userService = userService;
          this.userServiceLoaded$.next(true);
          this.loadCoverImage(this.userService?.cover_id);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loadingBar.fetchingStop();
        },
      });
  }

  public get isPaidMeeting(): boolean {
    return this.meeting && this.meeting.charging === BillingMeetingChargingEnum.fee;
  }

  public get isAuthor(): boolean {
    return this.meeting && this.meeting.visit_info && this.meeting.visit_info.is_author;
  }

  protected incrementView() {
    if (this.view_processed) {
      return;
    }
  }

  public get joinMeetingUrl(): string {
    return this.request.host + this.router.createUrlTree(ContentRouter.meetingJoinRoute(this.meeting)).toString();
  }

  public userpicUrl(user: User): string {
    return user.userpic && user.userpic.sm ? user.userpic.sm.url : null;
  }

  public getUserName(user: User): string {
    return AccountService.getUserName(user);
  }

  public roleName(role: MeetingRoleEnum): string {
    return (
      {
        [MeetingRoleEnum.observer]: 'Observer',
        [MeetingRoleEnum.owner]: 'Owner',
        [MeetingRoleEnum.participant]: 'Participant',
        [MeetingRoleEnum.reservation]: 'Reservation',
        [MeetingRoleEnum.waiter]: 'Waiter',
        [MeetingRoleEnum.left]: 'Left',
      }[role] || '--'
    );
  }

  public billingStatus(billingStatus: MeetingBillingStatusEnum): string {
    return (
      {
        [MeetingBillingStatusEnum.unpaid]: 'unpaid', // не оплачено
        [MeetingBillingStatusEnum.paid]: 'paid', // оплачено
        [MeetingBillingStatusEnum.free]: 'free', // бесплатно
        [MeetingBillingStatusEnum.processing]: 'processing', // в процессе оплаты
        [MeetingBillingStatusEnum.refunded]: 'refunded', // возвращено
        [MeetingBillingStatusEnum.failed]: 'failed', // не удалось
        [MeetingBillingStatusEnum.blocked]: 'blocked', // оплата зарезервирована
      }[billingStatus] || '--'
    );
  }

  public acceptStatus(acceptStatus: MeetingAcceptStatusEnum): string {
    return (
      {
        [MeetingAcceptStatusEnum.not_required]: 'not_required', // подтверждение не требутся
        [MeetingAcceptStatusEnum.required]: 'required', // требуется подтверждение и пока нет подтверждения
        [MeetingAcceptStatusEnum.accepted]: 'accepted', // участие подтверждено
        [MeetingAcceptStatusEnum.declined]: 'declined', // участие отклонено
      }[acceptStatus] || '--'
    );
  }

  public handleError(err) {
    // ??? TODO this.view = 'error';
    if (err instanceof NotFoundError) {
      this.view = 'not-found';
      this.meta.setTitle(['meetings.meeting-not-found']);
      // TODO remove meta
      if (this.response) {
        this.response.statusCode = 404;
        this.response.statusMessage = 'Page Not Found';
      }
    }
  }

  public writeTo(user_id) {
    this.chatService.openPrivateChat(user_id);
  }

  protected reload() {
    if (this.meeting) {
      this.reloadMeeting$.next(true);
    }
    if (this.userService) {
      this.reloadUserService$.next(true);
    }
  }

  private startWatching() {
    this.websocketService.startWatching('meeting', this.id);
    this.websocketService
      .onEntityUpdate('meeting', this.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.reloadMeeting$.next(true);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  private stopWatching() {
    this.websocketService.stopWatching('meeting', this.id);
  }

  private cover: IFileType;

  protected loadCoverImage(cover_id) {
    if (!cover_id) {
      this.cover = null;
      this.setupMeta();
      return;
    }

    if (this.cover?.id === cover_id) {
      return;
    }

    this.filesService
      .getFileById(cover_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (image) => {
          this.cover = image;
          this.setupMeta();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-FILE');
        },
      });
  }

  public get metaImage(): IImageThumbnail {
    if (this.cover) {
      return (
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        (this.cover.images || []).find((f) => f.size === ImageSizeEnum.lgX2) ||
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
        (this.cover.images || []).find((f) => f.size === ImageSizeEnum.original)
      );
    }
    return null;
  }

  private setupMeta() {
    if (this.view === 'meeting') {
      const title = this.meetingsService.meetingFullTitle(this.meeting);
      this.meta.setTitle([title, 'meetings.meta-title']);
      this.meta.setDescription(striptags(this.meeting.description));
      this.meta.setKeywords(this.meeting.keywords || []);

      const route = ContentRouter.meetingRoute(this.meeting);
      this.meta.setUrl(this.router.createUrlTree(route).toString());
    }

    if (this.view === 'user-service') {
      const title = this.userServicesService.userServiceFullTitle(this.userService);
      this.meta.setTitle([title, 'meetings.meta-title']);
      this.meta.setDescription(striptags(this.userService.description));
      this.meta.setKeywords(this.userService.keywords || []);

      const route = LibraryRouter.userServiceRouteUniversal(this.userService);
      this.meta.setUrl(this.router.createUrlTree([route]).toString());
    }

    if (this.cover?.id) {
      this.meta.setImage(this.cover.id);
    }
  }
}
